// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// D7.3: first-visit welcome bookkeeping (functional core). The welcome dialog is
// shown once and dismissal is remembered in localStorage, keyed per user, so it
// doesn't nag on every visit. The storage is injected (Pick<Storage,...>) so the
// decision logic is unit-testable without a browser and degrades safely when
// storage is unavailable (private mode / blocked). A server-side first-login
// flag would make this cross-device; that's a future enhancement (see
// docs/d7/d7-acceptance.md).

const WELCOME_PREFIX = 'sa.welcomed.';

// Pure: the per-user localStorage key.
export function welcomeStorageKey(userId: string): string {
  return `${WELCOME_PREFIX}${userId}`;
}

type WelcomeStore = Pick<Storage, 'getItem' | 'setItem'>;

// Whether the user has already dismissed the welcome. Fails safe to `true` (do
// not show) when we can't tell -- no user id, no storage, or storage throws --
// so a broken/blocked storage never causes the dialog to reappear every load.
export function hasSeenWelcome(userId: string, storage: WelcomeStore | undefined | null): boolean {
  if (!userId || !storage) {
    return true;
  }
  try {
    return storage.getItem(welcomeStorageKey(userId)) !== null;
  } catch {
    return true;
  }
}

// Record that the user has seen the welcome. Best-effort: a storage failure is
// swallowed (worst case the dialog shows again next visit).
export function markWelcomeSeen(userId: string, storage: WelcomeStore | undefined | null): void {
  if (!userId || !storage) {
    return;
  }
  try {
    storage.setItem(welcomeStorageKey(userId), '1');
  } catch {
    // ignore: private mode / quota / blocked storage
  }
}

// Pure: the first word of a display name, for a friendly greeting. Empty string
// when there's nothing usable (the dialog falls back to a generic greeting).
export function firstName(displayName: string): string {
  return (displayName ?? '').trim().split(/\s+/)[0] ?? '';
}
